/* Public, anonymous Promotion Studio banner. No tracking, cookies, or user data. */
(function (root) {
  'use strict';

  const HUB_ORIGIN = 'https://kachang-hub.onrender.com';
  const STORE_HOSTS = new Set([
    'vfb1m1-x7.myshopify.com',
    'technicaloutdoor.com',
    'www.technicaloutdoor.com',
  ]);
  const LIMITS = {
    id: 32,
    headline: 100,
    product_name: 180,
    body: 260,
    cta: 60,
    availability_text: 160,
    price_text: 48,
    product_url: 500,
    image_url: 500,
  };
  const POLL_MS = 60 * 1000;
  const REQUEST_TIMEOUT_MS = 8 * 1000;

  function languageOf(value) {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'id' || normalized.startsWith('id-')) return 'id';
    if (normalized === 'en' || normalized.startsWith('en-')) return 'en';
    return null;
  }

  function publicText(value, limit, required) {
    if (typeof value !== 'string') return null;
    const text = value.trim();
    if ((required && !text) || text.length > limit || /[\u0000-\u001f\u007f]/.test(text)) return null;
    return text;
  }

  function productUrl(value, base) {
    const text = publicText(value, LIMITS.product_url, true);
    if (!text) return null;
    try {
      const url = new URL(text, base);
      if (
        url.protocol !== 'https:' ||
        url.username ||
        url.password ||
        url.port ||
        !STORE_HOSTS.has(url.hostname.toLowerCase())
      ) return null;
      if (!/^\/(?:en\/)?products(?:\/|$)/.test(url.pathname)) return null;
      return url.href;
    } catch (_) {
      return null;
    }
  }

  function imageUrl(value, language, id) {
    if (value === null || value === undefined || value === '') return null;
    const text = publicText(value, LIMITS.image_url, false);
    if (!text) return null;
    try {
      const url = new URL(text);
      const expected = `/promotion-banner/${language}/${id}.png`;
      if (url.origin !== HUB_ORIGIN || url.pathname !== expected || url.search || url.hash) return null;
      return url.href;
    } catch (_) {
      return null;
    }
  }

  function validateCampaign(value, language, now, base) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    const id = publicText(value.id, LIMITS.id, true);
    if (!id || !/^[a-f0-9]{32}$/.test(id) || value.language !== language) return null;
    const expiresAt = Date.parse(value.expires_at);
    if (!Number.isFinite(expiresAt) || expiresAt <= now || expiresAt > now + 24 * 60 * 60 * 1000) return null;
    const campaign = {
      id,
      language,
      headline: publicText(value.headline, LIMITS.headline, true),
      product_name: publicText(value.product_name, LIMITS.product_name, true),
      body: publicText(value.body, LIMITS.body, true),
      cta: publicText(value.cta, LIMITS.cta, true),
      availability_text: publicText(value.availability_text, LIMITS.availability_text, false),
      price_text: publicText(value.price_text, LIMITS.price_text, false),
      product_url: productUrl(value.product_url, base),
      expires_at: expiresAt,
    };
    if (!campaign.headline || !campaign.product_name || !campaign.body || !campaign.cta || !campaign.product_url) return null;
    campaign.image_url = imageUrl(value.image_url, language, id);
    if (value.image_url && !campaign.image_url) return null;
    return campaign;
  }

  function text(node, value) {
    node.textContent = value;
  }

  function setup(section, documentRef, config) {
    const language = languageOf(documentRef.documentElement.lang);
    if (!language) return null;
    const nodes = {
      product: section.querySelector('[data-kp-product]'),
      headline: section.querySelector('[data-kp-headline]'),
      body: section.querySelector('[data-kp-body]'),
      price: section.querySelector('[data-kp-price]'),
      availability: section.querySelector('[data-kp-availability]'),
      cta: section.querySelector('[data-kp-cta]'),
      ctaText: section.querySelector('[data-kp-cta-text]'),
      imageWrap: section.querySelector('[data-kp-image-wrap]'),
      image: section.querySelector('[data-kp-image]'),
    };
    if (Object.values(nodes).some((node) => !node)) return null;

    const runtime = config.runtime;
    let expirationTimer = null;
    let pollTimer = null;
    let activeRequest = null;
    let requestNumber = 0;
    let imageNumber = 0;

    function visible() {
      return documentRef.visibilityState !== 'hidden';
    }

    function cancelRequest() {
      requestNumber += 1;
      if (activeRequest) activeRequest.abort();
      activeRequest = null;
    }

    function clearExpiration() {
      if (expirationTimer) runtime.clearTimeout(expirationTimer);
      expirationTimer = null;
    }

    function hide() {
      clearExpiration();
      imageNumber += 1;
      nodes.image.onerror = null;
      nodes.image.removeAttribute('src');
      nodes.image.alt = '';
      nodes.imageWrap.hidden = true;
      section.hidden = true;
    }

    function show(campaign) {
      text(nodes.product, campaign.product_name);
      text(nodes.headline, campaign.headline);
      text(nodes.body, campaign.body);
      text(nodes.ctaText, campaign.cta);
      nodes.cta.href = campaign.product_url;
      text(nodes.price, campaign.price_text || '');
      nodes.price.hidden = !campaign.price_text;
      text(nodes.availability, campaign.availability_text || '');
      nodes.availability.hidden = !campaign.availability_text;
      imageNumber += 1;
      const imageRequest = imageNumber;
      if (campaign.image_url) {
        nodes.image.onerror = function () {
          if (imageRequest === imageNumber) hide();
        };
        nodes.image.alt = campaign.product_name;
        nodes.image.src = campaign.image_url;
        nodes.imageWrap.hidden = false;
      } else {
        nodes.image.onerror = null;
        nodes.image.removeAttribute('src');
        nodes.image.alt = '';
        nodes.imageWrap.hidden = true;
      }
      section.hidden = false;
      clearExpiration();
      expirationTimer = runtime.setTimeout(hide, Math.max(0, campaign.expires_at - Date.now()));
    }

    function payloadWithTimeout(url, controller) {
      let timeout = null;
      const request = config.fetcher(url, {
        credentials: 'omit', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal,
      }).then((response) => response.ok ? response.json() : Promise.reject(new Error('feed unavailable')));
      const timed = new Promise((_, reject) => {
        timeout = runtime.setTimeout(function () {
          controller.abort();
          reject(new Error('feed timed out'));
        }, REQUEST_TIMEOUT_MS);
      });
      return Promise.race([request, timed]).finally(function () {
        if (timeout) runtime.clearTimeout(timeout);
      });
    }

    async function refresh() {
      if (!visible()) return;
      cancelRequest();
      const controller = new runtime.AbortController();
      activeRequest = controller;
      const request = requestNumber;
      try {
        const payload = await payloadWithTimeout(`${HUB_ORIGIN}/promotion-banner/${language}.json`, controller);
        if (request !== requestNumber || controller !== activeRequest) return;
        activeRequest = null;
        const campaign = validateCampaign(payload && payload.campaign, language, Date.now(), config.base);
        if (!campaign) hide();
        else show(campaign);
      } catch (_) {
        if (request === requestNumber && controller === activeRequest) {
          activeRequest = null;
          hide();
        }
      }
    }

    function startPolling() {
      if (!visible()) return;
      if (!pollTimer) pollTimer = runtime.setInterval(refresh, POLL_MS);
      refresh();
    }

    function stopPolling() {
      if (pollTimer) runtime.clearInterval(pollTimer);
      pollTimer = null;
      cancelRequest();
    }

    function visibilityChanged() {
      if (visible()) startPolling();
      else stopPolling();
    }

    documentRef.addEventListener('visibilitychange', visibilityChanged);
    startPolling();
    return {
      destroy: function () {
        stopPolling();
        clearExpiration();
        documentRef.removeEventListener('visibilitychange', visibilityChanged);
        hide();
      },
      refresh,
    };
  }

  function init(documentRef, suppliedConfig) {
    if (!documentRef || !documentRef.documentElement || !documentRef.querySelectorAll) return [];
    const browser = root || globalThis;
    const configured = suppliedConfig || {};
    const config = {
      base: configured.base || browser.location?.href || HUB_ORIGIN,
      fetcher: configured.fetcher || browser.fetch?.bind(browser),
      runtime: configured.runtime || browser,
    };
    if (typeof config.fetcher !== 'function' || typeof config.runtime.AbortController !== 'function') return [];
    return Array.from(documentRef.querySelectorAll('[data-kp-banner]'))
      .map((section) => setup(section, documentRef, config))
      .filter(Boolean);
  }

  const api = { HUB_ORIGIN, LIMITS, REQUEST_TIMEOUT_MS, imageUrl, languageOf, productUrl, validateCampaign, init };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (root && root.document) {
    root.KachangPromotionBanner = api;
    if (!root.document.documentElement.hasAttribute('data-kp-preview')) init(root.document);
  }
})(typeof window === 'undefined' ? null : window);
